// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_send_log/recap-season
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Climb data stays local; only a DERIVED-SIGNALS
// object (hardest send, attempts-before-tick, grade drift, indoor/outdoor split,
// ascent/style/discipline mixes, top venues/partners, most-worked projects — see
// signals.rs) is sent for THIS recap. Raw notes never leave the device.
//
// HARDEST guardrail (THE FENCE — enforced HERE pre-LLM + mirrored server-side + in UI copy):
// the recap NARRATES the climber's logged PAST only. It must NEVER advise, coach, prescribe a
// grade to try, give training plans, or touch finger-injury / any medical guidance. We do not
// rebuild the brain; we compute the signals, refuse coaching/medical requests BEFORE billing,
// call the recap endpoint, and render the result.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { SeasonSignals } from "./store";

const SERVER = "https://api.orioralabs.com";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "refused" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ── PRE-LLM REFUSAL FENCE ──────────────────────────────────────────────────────
// CAN-only, observational. We HARD-refuse coaching/medical asks BEFORE any billed
// call: "what grade should I try", "how to avoid finger injury", "training plan",
// technique help, "am I ready". This is the spec's hard fence, enforced where the
// spec says (pre-LLM). The season recap itself takes NO free-text from the user — it
// only narrates the derived signals — so the fence guards the optional free-text
// "ask" path and any future prompt surface. It runs deterministically in TS.
const COACHING_MEDICAL_PATTERNS: RegExp[] = [
  // grade-to-try / progression advice
  /\bwhat\s+grade\s+(should|can|could|do|to)\b/i,
  /\b(should|can|could)\s+i\s+(try|attempt|climb|push|project)\b/i,
  /\bwhat\s+(should|can|could)\s+i\s+(climb|try|project|work)\b/i,
  /\b(am|are)\s+i\s+ready\b/i,
  /\bnext\s+grade\b/i,
  // training / programming
  /\btraining\s+(plan|program|programme|schedule|advice|tips?)\b/i,
  // Coaching intent needs the "how do/can/should I" or "should I" prefix — otherwise bare
  // "improve"/"progress" over-blocked DESCRIPTIVE questions about the user's OWN log
  // ("did my grades improve this year?", "am I making progress on my projects?").
  /\b(how\s+(do|can|should)\s+i|should\s+i)\s+(train|get\s+(stronger|better)|improve|progress)\b/i,
  /\bhang\s*board|campus\s*board|periodi[sz]/i,
  /\bwarm[\s-]?up\s+(routine|plan)\b/i,
  // technique coaching
  /\bhow\s+(do|to)\s+i?\s*(climb|send|crimp|heel\s*hook|flag|dyno|beta)\b/i,
  /\bbeta\b.*\b(for|on)\b/i,
  /\bteach\s+me\b/i,
  /\bcoach\s+me\b/i,
  // "what should I do to send harder / climb V6" — coaching advice keyed to a climbing noun so
  // app-help asks ("what should I do next") aren't over-blocked.
  /\bwhat (should|can|do) i do\b[^?.!]{0,28}\b(send|sending|climb|project|grade|stronger|harder|better|improve|crux|beta|v\d|5\.\d)\b/i,
  // injury / medical — body-part THEN injury word, OR injury word THEN body-part (either order).
  /\b(finger|tendon|pulley|a2|shoulder|elbow|wrist|forearm)\b.*\b(injur|pain|hurt|tweak|strain|rehab|recover|heal|tape|rest)\b/i,
  /\b(injur|tweak|strain|hurt|pain|rehab|tape|rest(?:ing)?)\b.*\b(finger|tendon|pulley|a2|shoulder|elbow|wrist|forearm)\b/i,
  /\binjur(y|ies|ed)\b/i,
  /\bavoid\s+.*\binjur/i,
  /\b(rest|recovery|deload)\s+(day|week|advice|how)\b/i,
  /\bmedical|physio|doctor|diagnos/i,
];

const FENCE_MESSAGE =
  "Send Log only narrates the climbs you've logged — it doesn't give coaching, grade, training, or injury advice. Try asking about your own ticks (your hardest send, attempts before a tick, indoor vs outdoor).";

/** Returns true if the text asks for coaching/medical guidance and must be refused PRE-billing. */
export function isCoachingOrMedical(text: string): boolean {
  const t = (text || "").trim();
  if (!t) return false;
  return COACHING_MEDICAL_PATTERNS.some((re) => re.test(t));
}

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/oriora_send_log/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface SeasonRecap {
  recap: string;
}

/**
 * Season recap — the ONE billed AI feature.
 *
 * We compute the non-obvious STRUCTURE on-device (hardest send, attempts-before-tick,
 * grade drift, indoor/outdoor split, ascent/style/discipline mixes, top venues/partners,
 * most-worked projects — see store.ts / signals.rs) and send ONLY those aggregates. The
 * brain narrates specific, named-number observations about the season the climber logged.
 * Raw notes never leave the device.
 *
 * Endpoint: POST /api/apps/oriora_send_log/recap-season (Bearer JWT). The server prompt
 * stays OBSERVATIONAL + CAN-only — describes counts/splits/drift, NEVER advises, coaches,
 * prescribes a grade, or gives training/injury guidance (operator-side prompt; mirrored here).
 */
export async function recapSeason(signals: SeasonSignals): Promise<SeasonRecap> {
  if (signals.overall.total_climbs === 0) {
    throw new BrainError("unavailable", "Log a few climbs first — there's nothing to recap yet.");
  }
  const r = await brainCall<Partial<SeasonRecap>>("recap-season", { signals });
  return {
    recap: String(r.recap ?? "").trim(),
  };
}

/**
 * Optional free-text "ask my ticks" path (post-season-recap surface). Runs the PRE-LLM
 * fence first: any coaching/grade/training/injury request is refused with NO billed call.
 * Only descriptive questions about the logged corpus reach the brain.
 */
export async function askTicks(question: string, signals: SeasonSignals): Promise<SeasonRecap> {
  if (isCoachingOrMedical(question)) {
    throw new BrainError("refused", FENCE_MESSAGE);
  }
  if (signals.overall.total_climbs === 0) {
    throw new BrainError("unavailable", "Log a few climbs first — there's nothing to ask about yet.");
  }
  const r = await brainCall<Partial<SeasonRecap>>("ask-ticks", { question: question.trim(), signals });
  return { recap: String(r.recap ?? "").trim() };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_send_log/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/oriora_send_log/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/oriora_send_log/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile (mirrors expense_audit_ai). Session returns via auth:deep-link (App.tsx + ipc.rs).
// The web callback must allowlist `oriora-send-log` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");
